import * as THREE from 'three';

import workspace from "../../../../../canvas/core/workspace";
import { getShapeStartBounds } from "./cabinetPlacementResolver";

// Workspace cabinet dimensions are inch-based: keep free drag near the layout,
// while leaving a broad fixed area for kitchens started directly in 3D.
export const CABINET_FREE_DRAG_LAYOUT_PADDING = 240;
export const CABINET_EMPTY_LAYOUT_HALF_SIZE = 300;
export const CABINET_FREE_DRAG_BOUNDARY_WARNING_DISTANCE = 36;

const BOUNDARY_COLOR = 0x4aa6b8;
const BOUNDARY_OPACITY = 0.28;
const BOUNDARY_EDGE_COLOR = 0xe8fbff;
const BOUNDARY_EDGE_OPACITY = 0.42;
const BOUNDARY_RENDER_ORDER = 998;
const BOUNDARY_RIBBON_WIDTH = 9;
const BOUNDARY_HEIGHT = -0.015;

function expandBounds(bounds, padding) {
  return {
    left: bounds.left - padding,
    right: bounds.right + padding,
    top: bounds.top - padding,
    bottom: bounds.bottom + padding,
  };
}

function mergeBounds(firstBounds, secondBounds) {
  if (!firstBounds) {
    return secondBounds;
  }

  return {
    left: Math.min(firstBounds.left, secondBounds.left),
    right: Math.max(firstBounds.right, secondBounds.right),
    top: Math.min(firstBounds.top, secondBounds.top),
    bottom: Math.max(firstBounds.bottom, secondBounds.bottom),
  };
}

export function getCabinetFreeDragLayoutBounds(movingShape) {
  const layoutBounds = workspace.getShapes(true)
    .filter(shape => shape && shape !== movingShape && !shape.isHidden)
    .map(shape => getShapeStartBounds(shape))
    .filter(Boolean)
    .reduce((mergedBounds, shapeBounds) => mergeBounds(mergedBounds, shapeBounds), null);

  if (layoutBounds) {
    return expandBounds(layoutBounds, CABINET_FREE_DRAG_LAYOUT_PADDING);
  }

  return {
    left: -CABINET_EMPTY_LAYOUT_HALF_SIZE,
    right: CABINET_EMPTY_LAYOUT_HALF_SIZE,
    top: -CABINET_EMPTY_LAYOUT_HALF_SIZE,
    bottom: CABINET_EMPTY_LAYOUT_HALF_SIZE,
  };
}

export function getMovedShapeBounds(candidatePosition, startShapePosition, startShapeBounds) {
  const delta = {
    x: candidatePosition.x - startShapePosition.x,
    y: candidatePosition.y - startShapePosition.y,
  };

  return {
    left: startShapeBounds.left + delta.x,
    right: startShapeBounds.right + delta.x,
    top: startShapeBounds.top + delta.y,
    bottom: startShapeBounds.bottom + delta.y,
  };
}

export function clampCabinetPositionToLayoutBounds(candidatePosition, startShapePosition, startShapeBounds, layoutBounds) {
  if (!candidatePosition || !startShapePosition || !startShapeBounds || !layoutBounds) {
    return candidatePosition;
  }

  const candidateBounds = getMovedShapeBounds(candidatePosition, startShapePosition, startShapeBounds);
  const clampedPosition = { ...candidatePosition };

  if (candidateBounds.left < layoutBounds.left) {
    clampedPosition.x += layoutBounds.left - candidateBounds.left;
  } else if (candidateBounds.right > layoutBounds.right) {
    clampedPosition.x -= candidateBounds.right - layoutBounds.right;
  }

  if (candidateBounds.top < layoutBounds.top) {
    clampedPosition.y += layoutBounds.top - candidateBounds.top;
  } else if (candidateBounds.bottom > layoutBounds.bottom) {
    clampedPosition.y -= candidateBounds.bottom - layoutBounds.bottom;
  }

  return clampedPosition;
}

export function isSameWorkspacePosition(firstPosition, secondPosition) {
  return (
    firstPosition
    && secondPosition
    && Math.abs(firstPosition.x - secondPosition.x) < 0.001
    && Math.abs(firstPosition.y - secondPosition.y) < 0.001
  );
}

export function isBoundsNearLayoutBoundary(bounds, layoutBounds, warningDistance = CABINET_FREE_DRAG_BOUNDARY_WARNING_DISTANCE) {
  if (!bounds || !layoutBounds) {
    return false;
  }

  return Math.min(
    Math.abs(bounds.left - layoutBounds.left),
    Math.abs(layoutBounds.right - bounds.right),
    Math.abs(bounds.top - layoutBounds.top),
    Math.abs(layoutBounds.bottom - bounds.bottom)
  ) <= warningDistance;
}

function disposeGroup(group) {
  if (!group) {
    return;
  }

  group.traverse((child) => {
    if (child.geometry) {
      child.geometry.dispose();
    }
  });
}

export class CabinetFreeDragBoundaryPreview {
  constructor(scene) {
    this.scene = scene;
    this.group = null;
    this.ribbonMaterial = new THREE.MeshBasicMaterial({
      color: BOUNDARY_COLOR,
      transparent: true,
      opacity: BOUNDARY_OPACITY,
      side: THREE.DoubleSide,
      depthTest: true,
      depthWrite: false,
    });
    this.edgeMaterial = new THREE.LineBasicMaterial({
      color: BOUNDARY_EDGE_COLOR,
      transparent: true,
      opacity: BOUNDARY_EDGE_OPACITY,
      depthTest: true,
      depthWrite: false,
    });
  }

  clear() {
    if (!this.group) {
      return;
    }

    this.scene.remove(this.group);
    disposeGroup(this.group);
    this.group = null;
  }

  dispose() {
    this.clear();
    this.ribbonMaterial.dispose();
    this.edgeMaterial.dispose();
  }

  update({ bounds, modelScale, shouldShow }) {
    if (!shouldShow || !bounds) {
      this.clear();
      return;
    }

    this.clear();

    const scale = modelScale || 1;
    const ribbonWidth = BOUNDARY_RIBBON_WIDTH * scale;
    const left = bounds.left * scale;
    const right = bounds.right * scale;
    const top = bounds.top * scale;
    const bottom = bounds.bottom * scale;
    const width = Math.max(right - left, ribbonWidth);
    const depth = Math.max(bottom - top, ribbonWidth);
    const group = new THREE.Group();

    [
      { width, depth: ribbonWidth, x: (left + right) / 2, z: top },
      { width, depth: ribbonWidth, x: (left + right) / 2, z: bottom },
      { width: ribbonWidth, depth, x: left, z: (top + bottom) / 2 },
      { width: ribbonWidth, depth, x: right, z: (top + bottom) / 2 },
    ].forEach((part) => {
      const mesh = new THREE.Mesh(new THREE.PlaneGeometry(part.width, part.depth), this.ribbonMaterial);
      mesh.rotation.x = -Math.PI / 2;
      mesh.position.set(part.x, BOUNDARY_HEIGHT, part.z);
      group.add(mesh);
    });

    const edgeGeometry = new THREE.BufferGeometry().setFromPoints([
      new THREE.Vector3(left, BOUNDARY_HEIGHT + 0.002, top),
      new THREE.Vector3(right, BOUNDARY_HEIGHT + 0.002, top),
      new THREE.Vector3(right, BOUNDARY_HEIGHT + 0.002, bottom),
      new THREE.Vector3(left, BOUNDARY_HEIGHT + 0.002, bottom),
      new THREE.Vector3(left, BOUNDARY_HEIGHT + 0.002, top),
    ]);
    group.add(new THREE.Line(edgeGeometry, this.edgeMaterial));

    group.renderOrder = BOUNDARY_RENDER_ORDER;
    this.scene.add(group);
    this.group = group;
  }
}
